import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";

import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { PortfolioStatePage } from "@/components/site/PortfolioStatePage";
import { portfolioStateFaqJsonLd, portfolioStateMetaDescription, stateFaq } from "@/components/site/PortfolioStatePage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";
import { stateScenarioLocation } from "@/lib/state-location";

const SLUG = "portfolio-rental-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/portfolio-rental-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `${PRODUCT.shortName} | Simply Approved Mortgages` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = `${state.name} Portfolio Rental Loans | Blanket DSCR · 80% LTV | Simply Approved`;
    const description = portfolioStateMetaDescription(state);
    return buildHead({
      title,
      description,
      url,
      keywords: stateKeywordsMeta(PRODUCT.slug, state.name),
      image: stateHero.src,
      imageAlt: stateHero.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: state.name, url },
      ],
      faqs: stateFaq(state),
      service: { name: `Portfolio Rental Loans in ${state.name}`, state: state.name },
      extraJsonLd: [
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <PortfolioStatePage
      product={PRODUCT}
      state={state}
      liveStats={liveStats}
      housingMomentum={housingMomentum}
      countyInsights={countyInsights}
      topSlot={
        <LiveDscrRateSection
          strategyCards
          bare
          id="live-rates"
          eyebrow={`Live pricing · ${state.name}`}
          heading={`Live DSCR pricing on a single ${state.name} rental property`}
          paragraphs={[
            `These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property in ${state.name} - rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.`,
          ]}
          scenarioOverrides={{ ...stateScenarioLocation(state.abbrev) }}
          toolLabel={`${state.name} portfolio DSCR`}
          links={[
            { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
            { to: "/rates", label: "See today's investor rate bands" },
          ]}
        />
      }
      midSlot={
        <ProductReportCtaBanner productKey="portfolio" variant="full" locationLabel={state.name} />
      }

    />

  );
}
